import { faArrowUpRightFromSquare, faFileContract, faKey, faSave } from "@fortawesome/pro-solid-svg-icons"
import { Button, Form, Skeleton, Space, Typography } from "antd"
import { useContext } from "react"
import { AuthenticationContext } from "../../AuthenticationContext"
import { useCreateRolesMutation } from "../../domain/roles/rolesQueries"
import { useAuthenticateToTenant } from "../authentication/authenticationQueries"
import HeroButton from "../common/HeroButton"
import HeroButtonsContainer from "../common/HeroButtonsContainer"
import HeroContainer from "../common/HeroContainer"
import AdministratorAccountSection from "./TenantFormSections/AdministratorAccountSection"
import JobDefaultsSection from "./TenantFormSections/JobDefaultsSection"
import TenantDiagnosticsSection from "./TenantFormSections/TenantDiagnosticsSection"
import QboSettingsSection from "./TenantFormSections/QboSettingsSection"
import TenantInfoSection from "./TenantFormSections/TenantInfoSection"
import TenantInvoicingOptionsSection from "./TenantFormSections/TenantInvoicingOptionsSection"
import TenantLogoAndImagesSection from "./TenantFormSections/TenantLogoAndImagesSection"
import TenantRolesSection from "./TenantFormSections/TenantRolesSection"
import TenantsContext from "./TenantsContext"
import { toTenantForSave, TenantUpdateDTO } from "./tenantsModel"
import { useSaveTenantMutation } from "./tenantsQueries"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

type Props = {
  heroImage: string
  isMyCompanyPage?: boolean
  onTenantSavedSuccessfully?: (tenantId: number) => void
}

const TenantForm = ({ heroImage, isMyCompanyPage, onTenantSavedSuccessfully }: Props) => {
  const { state, dispatch, isSaveButtonDisabled, hasRolesChanged, isNewTenant: isNewUser, isUSQuickBooksAccount, isTenantDataLoading } = useContext(TenantsContext)
  const { currentUser } = useContext(AuthenticationContext)

  const saveTenantMutation = useSaveTenantMutation(onTenantSavedSuccessfully)
  const createRolesMutation = useCreateRolesMutation()
  const logIntoTenantMutation = useAuthenticateToTenant()

  const saveTenantData = () => {
    const tenantPayload: TenantUpdateDTO = {
      tenant: toTenantForSave(state.tenant),
      adminAccount: state.adminAccount,
      images: state.images
    }

    saveTenantMutation.mutate(tenantPayload)

    if (hasRolesChanged) {
      const rolesToSave = state.roles.filter(role => !role.removed)
      createRolesMutation.mutate({ tenantId: state.tenant.id, roles: rolesToSave })
      dispatch({ type: 'RESET_ROLES' })
    }
  }

  if (isTenantDataLoading) {
    return <Skeleton active />
  }

  return <div id="main" className={`main-template ${isMyCompanyPage ? '' : 'has-elements'} fluid-container`}>
    <HeroContainer imgSrc={heroImage} />
    <div className="hero-details">
      <h1>
        {state.tenant.name}
        <br />
        <small>SynkedUP Company</small>
      </h1>
      <HeroButtonsContainer>
        {
          currentUser?.isSuperUser && !isMyCompanyPage && !isNewUser && <HeroButton
            icon={faKey}
            onClick={() => logIntoTenantMutation.mutate(state.tenant.id)}
          />
        }
        <HeroButton
          icon={faSave}
          onClick={saveTenantData}
          disabled={isSaveButtonDisabled || saveTenantMutation.isLoading || createRolesMutation.isLoading}
        />
      </HeroButtonsContainer>
    </div>
    <Form layout="vertical">
      <TenantInfoSection />
      {
        currentUser?.isSuperUser && !isNewUser && <TenantDiagnosticsSection />
      }
      <JobDefaultsSection />
      {
        !state.tenant.hasAdminAccount && <AdministratorAccountSection />
      }
      {
        !isUSQuickBooksAccount && <QboSettingsSection />
      }
      <TenantRolesSection />
      <section>
        <Typography.Title level={5}><FontAwesomeIcon icon={faFileContract} /> Proposals</Typography.Title>
        <hr />
        <Space>
          <span>Proposal settings have been moved to the "Proposal Settings" section in the sidebar.</span>
          <Button
            type="link"
            onClick={() => window.open('/#!/proposal-settings', '_blank')}
            icon={<FontAwesomeIcon icon={faArrowUpRightFromSquare}
            />}>Go to Proposal Settings</Button>
        </Space>
      </section>
      <TenantInvoicingOptionsSection />
      <TenantLogoAndImagesSection />
    </Form>
  </div>
}

export default TenantForm